/* eslint-disable no-console */
import { useState, useEffect, useCallback } from "react";

import { Header } from "../../components/Header";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import { getEdificiosFull } from "../../utils";
import { getVideosSinPoster, backfillDryRun, backfillAplicar, DRYRUN_PREFIX } from "../../utils/backfillVideos";

const ROLES_BACKFILL = [ROLES.SUPERADMIN, ROLES.GERENCIA];

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const BackfillVideos = () => {
  const { rol } = useAuthContext();
  const autorizado = ROLES_BACKFILL.includes(rol);

  const [rows, setRows] = useState([]);
  const [edMap, setEdMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [modo, setModo] = useState(null); // 'dry' | 'apply'
  // resultados por fila: { estado: 'pendiente'|'procesando'|'ok'|'error', ...datos, error }
  const [res, setRes] = useState({});

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [videos, edificios] = await Promise.all([getVideosSinPoster(), getEdificiosFull()]);
      setRows(videos);
      setEdMap((edificios || []).reduce((acc, e) => ({ ...acc, [e.id]: e.edificio }), {}));
      setRes({});
    } catch (e) {
      console.error("Error cargando videos sin póster:", e);
      alert("Error cargando la lista: " + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autorizado) cargar();
    else setLoading(false);
  }, [autorizado, cargar]);

  // Corre el backfill fila por fila (secuencial). fn = backfillDryRun | backfillAplicar.
  const correr = useCallback(
    async (fn, etiqueta) => {
      setRunning(true);
      setModo(etiqueta);
      for (const row of rows) {
        setRes((r) => ({ ...r, [row.id]: { estado: "procesando" } }));
        try {
          const out = await fn(row);
          setRes((r) => ({ ...r, [row.id]: { estado: "ok", ...out } }));
        } catch (e) {
          // 10.177: no tragarse el error — loguear y marcar la fila que falló, seguir con las demás.
          console.error(`Backfill fila ${row.id} falló:`, e);
          setRes((r) => ({ ...r, [row.id]: { estado: "error", error: e?.message || String(e) } }));
        }
      }
      setRunning(false);
    },
    [rows]
  );

  const onDryRun = () => correr(backfillDryRun, "dry");

  const onAplicar = () => {
    // Guardarraíl: confirmación explícita; no se dispara solo.
    const ok = window.confirm(
      `APLICAR backfill en PRODUCCIÓN sobre ${rows.length} video(s).\n\n` +
        "• Sube el mp4 sin audio y el póster a paths NUEVOS (no sobrescribe los originales).\n" +
        "• Actualiza cada fila (url + poster_url).\n\n¿Continuar?"
    );
    if (ok) correr(backfillAplicar, "apply");
  };

  if (!autorizado) {
    return (
      <div style={{ padding: 15 }}>
        <Header />
        <div className="container-fluid px-4 py-3">
          <p className="text-danger fs-5">No autorizado. Solo SUPERADMIN / GERENCIA.</p>
        </div>
      </div>
    );
  }

  const link = (url, txt) =>
    url ? (
      <a href={url} target="_blank" rel="noreferrer">
        {txt}
      </a>
    ) : (
      "—"
    );

  const badge = (estado) => {
    const map = {
      procesando: "bg-warning text-dark",
      ok: "bg-success",
      error: "bg-danger",
    };
    return <span className={`badge ${map[estado] || "bg-secondary"}`}>{estado || "pendiente"}</span>;
  };

  return (
    <div style={{ padding: 15 }}>
      <Header />
      <div className="container-fluid px-4 py-3">
        <h4 className="mb-1">Backfill de videos — quitar audio + generar póster</h4>
        <p className="text-muted" style={{ fontSize: "0.9rem" }}>
          Reprocesa filas <code>tipo=&apos;video&apos;</code> sin <code>poster_url</code>. El <strong>dry-run</strong>{" "}
          sube a <code>{DRYRUN_PREFIX}/</code> (temporal, no toca nada de producción) para inspección.{" "}
          <strong>Aplicar</strong> sube a paths nuevos y actualiza las filas (preserva los originales).
        </p>

        <div className="d-flex align-items-center gap-2 mb-3">
          <button className="btn btn-outline-secondary btn-sm" onClick={cargar} disabled={loading || running}>
            Recargar lista
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={onDryRun}
            disabled={loading || running || rows.length === 0}
          >
            {running && modo === "dry" ? "Simulando…" : "Simular (dry-run)"}
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={onAplicar}
            disabled={loading || running || rows.length === 0}
          >
            {running && modo === "apply" ? "Aplicando…" : "Aplicar (producción)"}
          </button>
          <span className="text-muted" style={{ fontSize: "0.85rem" }}>
            {loading ? "Cargando…" : `${rows.length} video(s) sin póster`}
          </span>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-sm align-middle" style={{ fontSize: "0.82rem" }}>
            <thead className="table-dark">
              <tr>
                <th>ID</th>
                <th>Edificio</th>
                <th>Original</th>
                <th>MP4 nuevo (sin audio)</th>
                <th>Póster</th>
                <th className="text-end">Antes</th>
                <th className="text-end">Después</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const r = res[row.id] || {};
                return (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{edMap[row.idedificio] || row.idedificio}</td>
                    <td>{link(row.url, "original")}</td>
                    <td>{link(r.urlNuevoVideo, "mp4")}</td>
                    <td>{link(r.urlPoster, "póster")}</td>
                    <td className="text-end">{fmtBytes(r.tamOriginal)}</td>
                    <td className="text-end">{fmtBytes(r.tamSinAudio)}</td>
                    <td>
                      {badge(r.estado)}
                      {r.error && (
                        <div className="text-danger" style={{ fontSize: "0.72rem" }}>
                          {r.error}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-3">
                    No hay videos sin póster.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BackfillVideos;
